import React, { useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/data";

// Full-page parallax backdrop. Depth layers move at different speeds:
// arena image -> light beams -> neon blobs -> particle field -> smoke accent.
export const ParallaxBackground = () => {
  const { scrollYProgress } = useScroll();

  const arenaY = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);
  const arenaScale = useTransform(scrollYProgress, [0, 1], [1.12, 1.32]);
  const beamsY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const blobsY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const dotsY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const smokeAY = useTransform(scrollYProgress, [0, 1], ["-4%", "22%"]);
  const smokeBY = useTransform(scrollYProgress, [0, 1], ["8%", "-24%"]);
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);

  const dots = useMemo(
    () =>
      Array.from({ length: 34 }).map((_, i) => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 1 + Math.random() * 2.5,
        color: ["#ff00d8", "#00f7ff", "#7800ff"][i % 3],
        dur: 4 + Math.random() * 6,
        delay: -Math.random() * 6,
      })),
    []
  );

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden bg-void" data-testid="parallax-background">
      {/* Deepest layer: arena image */}
      <motion.div style={{ y: arenaY, scale: arenaScale }} className="absolute inset-0">
        <img src={ASSETS.heroAtmosphere} alt="" className="h-full w-full object-cover opacity-[0.30]" />
        <div className="absolute inset-0 bg-gradient-to-b from-void/30 via-void/60 to-void" />
      </motion.div>

      {/* Volumetric light beams */}
      <motion.div style={{ y: beamsY }} className="absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-1/4 left-[15%] h-[150%] w-[36rem] -rotate-12 opacity-40 animate-pulseglow"
          style={{ background: "linear-gradient(180deg, rgba(255,0,216,0.25), transparent 70%)", filter: "blur(30px)" }}
        />
        <div
          className="absolute -top-1/4 right-[12%] h-[150%] w-[32rem] rotate-12 opacity-40 animate-pulseglow"
          style={{ background: "linear-gradient(180deg, rgba(0,247,255,0.22), transparent 70%)", filter: "blur(34px)", animationDelay: "-1.5s" }}
        />
      </motion.div>

      {/* Neon blobs */}
      <motion.div style={{ y: blobsY }} className="absolute inset-0">
        <div className="absolute -top-40 -left-32 h-[42rem] w-[42rem] rounded-full bg-neon-violet/20 blur-[120px] animate-floaty" />
        <div className="absolute top-1/3 -right-40 h-[38rem] w-[38rem] rounded-full bg-neon-pink/15 blur-[130px] animate-floaty" style={{ animationDelay: "-4s" }} />
        <div className="absolute bottom-0 left-1/4 h-[34rem] w-[34rem] rounded-full bg-neon-cyan/12 blur-[130px] animate-floaty" style={{ animationDelay: "-8s" }} />
      </motion.div>

      {/* Floating neon particle field */}
      <motion.div style={{ y: dotsY }} className="absolute inset-0">
        {dots.map((d, i) => (
          <span
            key={i}
            className="absolute rounded-full animate-pulseglow"
            style={{
              left: `${d.left}%`,
              top: `${d.top}%`,
              width: d.size,
              height: d.size,
              background: d.color,
              boxShadow: `0 0 ${d.size * 4}px ${d.color}`,
              animationDuration: `${d.dur}s`,
              animationDelay: `${d.delay}s`,
            }}
          />
        ))}
      </motion.div>

      {/* Smoke — subtle accent only */}
      <motion.div style={{ y: smokeAY }} className="absolute inset-x-0 -top-1/4 h-[150%]">
        <img src={ASSETS.smokeCyan} alt="" className="smoke-drift-a h-full w-full object-cover opacity-[0.14]" style={{ mixBlendMode: "screen" }} />
      </motion.div>
      <motion.div style={{ y: smokeBY }} className="absolute inset-x-0 top-0 h-[160%]">
        <img src={ASSETS.smokePink} alt="" className="smoke-drift-b h-full w-full object-cover opacity-[0.12]" style={{ mixBlendMode: "screen" }} />
      </motion.div>

      {/* Neon grid + grain */}
      <motion.div
        style={{
          y: gridY,
          backgroundImage:
            "linear-gradient(rgba(255,0,216,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,247,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(circle at 50% 30%, black, transparent 80%)",
        }}
        className="absolute inset-0 opacity-[0.06]"
      />
      <div className="absolute inset-0 grain" />
    </div>
  );
};
